import React, { useState, useEffect } from 'react';
import { 
  MapPin, 
  User, 
  Phone, 
  Package, 
  Calendar, 
  DollarSign, 
  Truck, 
  Save, 
  Printer, 
  RefreshCw,
  Plus,
  Trash2
} from 'lucide-react';
import MainService from '../services/mainservice';
import { useToast } from '../contexts/ToastContext';
import CustomerAutoComplete from './CustomerAutoComplete';
import { getConfig } from '../services/config';

const NewOrder = () => {
  const [formData, setFormData] = useState({
    pickupAddress: '',
    clientBillNo: '',
    voucherNo: '',
    type: '',
    payType: 'CAD',
    customer: '',
    customerMobile: '',
    emirate: '',
    location: '',
    poBox: '',
    address: '',
    quantity: 1,
    remarks: '',
    pickupDate: '',
    deliveryDate: '',
    productPrice: 0,
    forPickup: true,
    exchange: false,
    fragile: false
  });

  const [addresses, setAddresses] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [types, setTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [selectedClient, setSelectedClient] = useState({
    MobileNo1: '',
    Id: '',
    Name: '',
    AddressId: ''
  });
  const { showSuccess, showError, showWarning } = useToast();

  useEffect(() => {
    initializeData();
  }, []);

  // Handle customer selection from auto-complete
  const handleCustomerSelect = (customer) => {
    if (customer) {
      setFormData(prev => ({
        ...prev,
        customer: customer.Name || '',
        customerMobile: customer.MobileNo1 || '',
        emirate: customer.EmirateId || '',
        location: customer.LocationName || '',
        address: customer.Address || '',
        poBox: customer.POBOX || ''
      }));
    }
  };

  const initializeData = async () => {
    try {
      setIsLoading(true);
      
      // Set default dates
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      setFormData(prev => ({
        ...prev,
        pickupDate: today.toISOString().split('T')[0],
        deliveryDate: tomorrow.toISOString().split('T')[0]
      }));

      // Get token info from localStorage
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      if (tokenData) {
        const userData = JSON.parse(tokenData);
        const clientData = {
          MobileNo1: userData.MobileNo1,
          Id: userData.ClientId,
          Name: userData.Name,
          Token: userData.Token,
          AddressId: ''
        };
        setSelectedClient(clientData);

        // Load addresses after setting client
        try {
          const addressesResponse = await MainService.GetClientAddress(clientData.Id);
          setAddresses(addressesResponse || []);
        } catch (error) {
          console.error('Error loading addresses:', error);
          setAddresses([]);
          showError('Failed to load pickup addresses');
        }
      }

      // Load emirates
      try {
        const emiratesResponse = await MainService.EmiratesInDropDown();
        setEmirates(emiratesResponse || []);
      } catch (error) {
        console.error('Error loading emirates:', error);
        setEmirates([]);
        showError('Failed to load emirates');
      }

      // Load types
      try {
        const typesResponse = await MainService.TypesInDropDown();
        setTypes(typesResponse || []);
      } catch (error) {
        console.error('Error loading types:', error);
        setTypes([]);
        showError('Failed to load types');
      }

      // Load next order number
      try {
        const orderNoResponse = await MainService.GetNextOrderNo();
        setFormData(prev => ({
          ...prev,
          voucherNo: orderNoResponse || ''
        }));
      } catch (error) {
        console.error('Error loading next order number:', error);
        showError('Failed to load next order number');
      }

    } catch (error) {
      console.error('Error initializing data:', error);
      showError('Failed to load initial data. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateTotals = () => {
    const subtotal = parseFloat(formData.productPrice);
    const tax = subtotal * 0.05;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Handle form submission
  };

  const transformOrderData = () => {
    const pickupDate = new Date(formData.pickupDate);
    const deliveryDate = new Date(formData.deliveryDate);
    
    // Find selected type ID
    const selectedType = types.find(type => type.Name === formData.type);
    
    return {
      VoucherNo: formData.voucherNo,
      ClientBillNo: formData.clientBillNo,
      ChargeFrom: "Client",
      PayType: formData.payType,
      CustomerName: formData.customer,
      MobileNo: formData.customerMobile,
      MobileNo2: formData.customerMobile, // Using same mobile for both fields
      Location: formData.location,
      Address: formData.address,
      Remarks: formData.remarks,
      Quantity: formData.quantity,
      ProductPrice: formData.productPrice,
      ClientId: selectedClient.Id,
      EmirateId: formData.emirate,
      ClientAddressId: selectedClient.AddressId,
      TypeId: selectedType?.Id || '',
      IsPickup: formData.forPickup,
      IsFragile: formData.fragile,
      IsExchange: formData.exchange,
      PMonth: pickupDate.getMonth() + 1,
      PDay: pickupDate.getDate(),
      PYear: pickupDate.getFullYear(),
      DMonth: deliveryDate.getMonth() + 1,
      DDay: deliveryDate.getDate(),
      DYear: deliveryDate.getFullYear()
    };
  };

  const validateForm = () => {
    if (!formData.pickupAddress) {
      showError('Please select a pickup address');
      return false;
    }
    if (!formData.customer) {
      showError('Please enter customer name');
      return false;
    }
    if (!formData.customerMobile) {
      showError('Please enter customer mobile number');
      return false;
    }
    if (!formData.emirate) {
      showError('Please select an emirate');
      return false;
    }
    if (!formData.location) {
      showError('Please enter location');
      return false;
    }
    if (!formData.address) {
      showError('Please enter address');
      return false;
    }
    if (!formData.type) {
      showError('Please select a type');
      return false;
    }
    if (formData.quantity <= 0) {
      showError('Please enter a valid quantity');
      return false;
    }
    if (formData.productPrice < 0) {
      showError('Please enter a valid product price');
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);
      
      const orderData = transformOrderData();
      const response = await MainService.SavePortalOrder(orderData, selectedClient.Token);
      
      if (response.Status === 'Success') {
        showSuccess('Order saved successfully!');
        handleRefresh(); // Reset form after successful save
      } else {
        showError(response.Message || 'Failed to save order');
      }
    } catch (error) {
      console.error('Error saving order:', error);
      showError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndPrint = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);
      
      const orderData = transformOrderData();
      const response = await MainService.SavePortalOrder(orderData, selectedClient.Token);
      
      if (response.Status === 'Success') {
        showSuccess('Order saved successfully!');
        
        // Print the order using the OrderId from response
        if (response.OrderIds != null) {
          await handlePrintOrder(response.OrderIds[0]);
        } else {
          showError('Order ID not found in response');
        }
        
        handleRefresh(); // Reset form after successful save
      } else {
        showError(response.Message || 'Failed to save order');
      }
    } catch (error) {
      console.error('Error saving order:', error);
      showError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePrintOrder = async (orderId) => {
    try {
      setIsPrinting(true);
      const response = await MainService.PrintOrder(orderId);
      
      if (response) {
        // Open the PDF in a new tab
        const pdfUrl = `${getConfig().API_BASE_URL}ReportFiles/${response}`;
        window.open(pdfUrl, '_blank');
        showSuccess('PDF generated successfully');
      } else {
        showError('Print Error - No data received');
      }
    } catch (error) {
      console.error('Print Error:', error);
      showError('Print Error - Failed to generate PDF');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleRefresh = async () => {
    setFormData({
      pickupAddress: '',
      clientBillNo: '',
      voucherNo: '',
      type: '',
      payType: 'CAD',
      customer: '',
      customerMobile: '',
      emirate: '',
      location: '',
      poBox: '',
      address: '',
      quantity: 1,
      remarks: '',
      pickupDate: new Date().toISOString().split('T')[0],
      deliveryDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
      productPrice: 0,
      forPickup: true,
      exchange: false,
      fragile: false
    });
    setSelectedClient(prev => ({ ...prev, AddressId: '' }));
    
    // Get new voucher number
    try {
      const orderNoResponse = await MainService.GetNextOrderNo();
      setFormData(prev => ({
        ...prev,
        voucherNo: orderNoResponse || ''
      }));
    } catch (error) {
      console.error('Error loading next order number:', error);
      showError('Failed to load next order number');
    }
    
    initializeData();
  };

  const { subtotal, tax, total } = calculateTotals();

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-primary-600">Loading pickup addresses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">New Delivery Order</h1>
              <p className="text-gray-600 mt-2">Create a new delivery order for your client</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleRefresh}
                className="btn-secondary flex items-center space-x-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Order Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Pickup Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <MapPin className="w-5 h-5 text-primary-600 mr-2" />
                  Pickup Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pickup Address
                    </label>
                    <select
                      value={formData.pickupAddress}
                      onChange={(e) => {
                        setFormData({...formData, pickupAddress: e.target.value});
                        setSelectedClient(prev => ({ ...prev, AddressId: e.target.value }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={addresses.length === 0}
                    >
                      <option value="">
                        {addresses.length === 0 ? 'No addresses available' : 'Choose Pickup Address'}
                      </option>
                      {addresses.map(addr => (
                        <option key={addr.Id} value={addr.Id}>
                          {addr.Address} - {addr.Location?.Name} - {addr.Emirate?.Name}
                        </option>
                      ))}
                    </select>
                    {addresses.length === 0 && (
                      <p className="text-xs text-red-500 mt-1">Please contact support to add addresses</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pickup Date
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={formData.pickupDate}
                        onChange={(e) => setFormData({...formData, pickupDate: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Details */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Package className="w-5 h-5 text-primary-600 mr-2" />
                  Order Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client Bill No
                    </label>
                    <input
                      type="text"
                      value={formData.clientBillNo}
                      onChange={(e) => setFormData({...formData, clientBillNo: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter bill number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Voucher No
                    </label>
                    <input
                      type="text"
                      value={formData.voucherNo}
                      onChange={(e) => setFormData({...formData, voucherNo: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter voucher number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={types.length === 0}
                    >
                      <option value="">
                        {types.length === 0 ? 'Loading types...' : 'Select Type'}
                      </option>
                      {types.map(type => (
                        <option key={type.Id} value={type.Name}>{type.Name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="w-5 h-5 text-primary-600 mr-2" />
                  Customer Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customer Name
                    </label>
                    <CustomerAutoComplete
                      value={formData.customer}
                      onChange={(value) => setFormData({...formData, customer: value})}
                      onCustomerSelect={handleCustomerSelect}
                      placeholder="Search customer name..."
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      value={formData.customerMobile}
                      onChange={(e) => setFormData({...formData, customerMobile: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter mobile number"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emirate
                    </label>
                    <select
                      value={formData.emirate}
                      onChange={(e) => setFormData({...formData, emirate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      disabled={emirates.length === 0}
                    >
                      <option value="">
                        {emirates.length === 0 ? 'Loading emirates...' : 'Select Emirate'}
                      </option>
                      {emirates.map(emirate => (
                        <option key={emirate.Id} value={emirate.Id}>{emirate.Name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter location"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PO Box
                    </label>
                    <input
                      type="text"
                      value={formData.poBox}
                      onChange={(e) => setFormData({...formData, poBox: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter PO Box"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Address
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter complete address"
                  />
                </div>
              </div>

              {/* Additional Details */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Truck className="w-5 h-5 text-primary-600 mr-2" />
                  Additional Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantity
                    </label>
                    <input
                      type="number"
                      value={formData.quantity}
                      onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 1})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pay Type
                    </label>
                    <select
                      value={formData.payType}
                      onChange={(e) => setFormData({...formData, payType: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="CAD">CAD</option>
                      <option value="COD">COD</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Remarks
                  </label>
                  <textarea
                    value={formData.remarks}
                    onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter any special instructions or remarks"
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Actions */}
            <div className="space-y-6">
              {/* Product Price */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  Product Price
                </h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Price (AED)
                  </label>
                  <input
                    type="number"
                    value={formData.productPrice}
                    onChange={(e) => setFormData({...formData, productPrice: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>

              {/* Options */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Options</h2>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.exchange}
                      onChange={(e) => setFormData({...formData, exchange: e.target.checked})}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Exchange?</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.fragile}
                      onChange={(e) => setFormData({...formData, fragile: e.target.checked})}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Fragile?</span>
                  </label>
                </div>
              </div>

              {/* Delivery Date */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Delivery Schedule</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Delivery Date
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={formData.deliveryDate}
                      onChange={(e) => setFormData({...formData, deliveryDate: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent cursor-pointer"
                      min={formData.pickupDate || new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="w-full btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Save Order</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAndPrint}
                    disabled={isSaving || isPrinting}
                    className="w-full btn-secondary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : isPrinting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Generating PDF...</span>
                      </>
                    ) : (
                      <>
                        <Printer className="w-4 h-4" />
                        <span>Save & Print</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewOrder;
